import React from 'react';
import Time from 'react-time';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import ProjectIcon from '../../icons/ProjectIcon.jsx';
import RatingLabel from '../../RatingLabel.jsx';
import {
  IMPACT_ACTION_PLAN_STATUSES,
  IMPACT_PERCEPTION,
  IMPACT_SCALE,
} from '../../../lib/schema.js';
import VerticalSeparator from '../../VerticalSeparator.jsx';
import { PieOutlinedIcon } from '../../icons/PieOutlinedIcon.jsx';
import { ShowMoreItemsHeader } from '../ChangeStrategy/ChangeStrategyOverview/ShowMoreItemsHeader.jsx';
import PortfolioIcon from '../../icons/PorfolioIcon.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import { PortfolioInfoLink } from '../../links/index.jsx';

const ChangeImpactSummary = ({ changeImpact, project, portfolio }) => {
  const {
    title,
    owners,
    peopleImpacted,
    startDate,
    endDate,
    changeImpactType,
    impactType,
    scale,
    perception,
    actionPlanTypes,
    actionPlanStatus,
  } = changeImpact;
  return (
    <div
      className="component component-bg-white"
      style={{ paddingBottom: '6px' }}
    >
      <div className="flex m-b-20">
        <div className="flex">
          <PortfolioIcon />
          <strong className="text-muted m-r-12 m-l-8">
            {getOrgTermsContent('portfolio')}:{' '}
          </strong>
          <strong>
            {portfolio ? (
              <PortfolioInfoLink portfolioId={portfolio.id}>
                {portfolio.name}
              </PortfolioInfoLink>
            ) : (
              '—'
            )}
          </strong>
        </div>
        <VerticalSeparator />
        <div className="flex m-l-20">
          <ProjectIcon />
          <strong className="text-muted m-l-8 m-r-12">
            {getOrgTermsContent('project')}:
          </strong>
          <strong>{project.name}</strong>
        </div>
      </div>

      <div className="flex flex-wrap">
        <div className="flex m-b-20">
          <PieOutlinedIcon />
          <strong className="text-muted m-l-8 m-r-12">Change Impact:</strong>
          <strong>{title}</strong>
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex flex-wrap m-b-20">
          <strong className="text-muted m-r-12">
            {pluralize('Owner', owners.length)}:
          </strong>

          {owners.length ? (
            <ShowMoreItemsHeader
              items={owners.map((owner) => ({
                ...owner,
                name: `${owner.name}`,
              }))}
              maxShowNumber={2}
            />
          ) : (
            '—'
          )}
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">
            Number of People Impacted:
          </strong>
          <strong>{peopleImpacted || '—'}</strong>
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">Dates: </strong>
          <strong>
            {startDate ? <Time value={startDate} format="M/D/YY" /> : null}
            <span> {startDate ? '-' : '—'} </span>
            {endDate ? <Time value={endDate} format="M/D/YY" /> : null}
          </strong>
        </div>
      </div>

      <div className="flex flex-wrap">
        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">Impact Type:</strong>
          {changeImpactType ? (
            <strong className={changeImpactType.archived ? 'text-muted' : ''}>
              {impactType}
            </strong>
          ) : (
            '—'
          )}
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">Scale:</strong>
          {scale ? <RatingLabel reverse rating={scale.toLowerCase()} /> : '—'}
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">Perception:</strong>
          {perception ? <RatingLabel rating={perception.toLowerCase()} /> : '—'}
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">
            Action Plan
            {pluralize(' Type', actionPlanTypes ? actionPlanTypes.length : 0)}:
          </strong>
          <strong>
            {actionPlanTypes
              ? actionPlanTypes.map((type, index) => (
                  <span key={index} className="text-capitalize">
                    {type}
                    {actionPlanTypes.length > 1 &&
                    actionPlanTypes.length - 1 !== index
                      ? ', '
                      : ' '}
                  </span>
                ))
              : '—'}
          </strong>
          <VerticalSeparator customClasses="m-l-18 m-r-18" />
        </div>

        <div className="flex m-b-20">
          <strong className="text-muted m-r-12">Action Plan Status:</strong>
          <strong className="text-capitalize">{actionPlanStatus || '—'}</strong>
        </div>
      </div>
    </div>
  );
};

ChangeImpactSummary.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,

  changeImpact: PropTypes.shape({
    impactType: PropTypes.string.isRequired,
    scale: PropTypes.oneOf(IMPACT_SCALE).isRequired,
    actionPlanTypes: PropTypes.arrayOf(PropTypes.string),
    actionPlanStatus: PropTypes.oneOf(IMPACT_ACTION_PLAN_STATUSES),
    perception: PropTypes.oneOf(IMPACT_PERCEPTION).isRequired,
  }).isRequired,
};

export default ChangeImpactSummary;
